// ResetPasswordModal — admin rotates an agent's password.
//
// Extracted (2026-07-04) so BOTH the agents list and the agent-detail page
// use one working implementation. The agent-detail "Reset password" action
// was previously firing the endpoint with NO body, which the server rejects
// (`new_password` is required), so admins couldn't reset an agent's password
// from that page at all. This modal collects + validates the password and
// POSTs { new_password } — the server schema is the source of truth.
//
// The endpoint also signs the agent out of every device; the admin shares
// the new credential out-of-band (WhatsApp / call).

import { useState } from 'react';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { INPUT_CLS } from './fieldStyles';

export default function ResetPasswordModal({
  agentId, username, onClose, onDone,
}: {
  agentId: number;
  username: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [pw, setPw] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const tooShort = pw.length > 0 && pw.length < 8;
  const noLetter = pw.length > 0 && !/[A-Za-z]/.test(pw);
  const noDigit  = pw.length > 0 && !/\d/.test(pw);
  const valid    = pw.length >= 8 && /[A-Za-z]/.test(pw) && /\d/.test(pw);

  function suggest() {
    const alpha = 'ABCDEFGHJKLMNPQRTUVWXYZabcdefghjkmnpqrtuvwxyz';
    const digs  = '23456789';
    const chars: string[] = [];
    chars.push(alpha[Math.floor(Math.random() * alpha.length)]);
    chars.push(digs[Math.floor(Math.random() * digs.length)]);
    const pool = alpha + digs;
    while (chars.length < 12) chars.push(pool[Math.floor(Math.random() * pool.length)]);
    for (let i = chars.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [chars[i], chars[j]] = [chars[j], chars[i]];
    }
    setPw(chars.join(''));
    setShow(true);
  }

  async function submit() {
    if (!valid || busy) return;
    setBusy(true); setErr(null);
    try {
      await adminApi.post(`/admin/agents/${agentId}/reset-password`, { new_password: pw });
      onDone();
    } catch (e) {
      setErr((e as ApiError).message || 'Failed to reset password');
    } finally {
      setBusy(false);
    }
  }

  function copy() {
    try { void navigator.clipboard.writeText(pw); } catch { /* best-effort */ }
  }

  return (
    <div className="fixed inset-0 z-[85] grid place-items-start sm:place-items-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm overflow-y-auto" onClick={onClose} role="dialog" aria-modal="true">
      <div
        className="w-full max-w-md rounded-2xl bg-white dark:bg-arena-800 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 p-5 space-y-3 shadow-2xl my-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-bold text-lg text-neutral-900 dark:text-white inline-flex items-center gap-2">
          <span className="grid place-items-center w-9 h-9 rounded-xl bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300">
            <Icon name="password" size={18} filled />
          </span>
          Reset password — @{username}
        </h3>
        <p className="text-[12.5px] text-arena-700 dark:text-neon-200/75 leading-snug">
          This rotates the agent's password AND signs them out of every device. Share the new credential
          out-of-band (WhatsApp / call). The agent must use this exact string to sign in again.
        </p>

        <div>
          <div className="flex items-center justify-between">
            <span className="block text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/70 font-semibold">
              New password
            </span>
            <button onClick={suggest} disabled={busy} type="button"
              className="text-[11px] font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300 hover:underline disabled:opacity-40">
              <Icon name="autorenew" size={11} className="inline align-text-bottom mr-0.5" />
              Suggest
            </button>
          </div>
          <div className="relative mt-1">
            <input
              type={show ? 'text' : 'password'}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              autoComplete="new-password"
              spellCheck={false}
              placeholder="8+ chars · letter + digit"
              disabled={busy}
              className={INPUT_CLS + ' font-mono pr-20'}
            />
            <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
              <button onClick={() => setShow((v) => !v)} type="button" disabled={busy}
                className="p-1.5 rounded-full text-arena-600 dark:text-neon-200/65 hover:bg-arena-100/60 dark:hover:bg-arena-700/60 disabled:opacity-40"
                aria-label={show ? 'Hide password' : 'Show password'}>
                <Icon name={show ? 'visibility_off' : 'visibility'} size={16} />
              </button>
              <button onClick={copy} type="button" disabled={busy || !pw}
                className="p-1.5 rounded-full text-arena-600 dark:text-neon-200/65 hover:bg-arena-100/60 dark:hover:bg-arena-700/60 disabled:opacity-40"
                aria-label="Copy password">
                <Icon name="content_copy" size={15} />
              </button>
            </div>
          </div>
          <ul className="text-[11px] mt-1.5 space-y-0.5">
            <li className={pw.length === 0 ? 'text-arena-500 dark:text-neon-200/55' : tooShort ? 'text-rose-600 dark:text-rose-300' : 'text-emerald-600 dark:text-emerald-300'}>
              <Icon name={pw.length === 0 ? 'radio_button_unchecked' : tooShort ? 'cancel' : 'check_circle'} size={11} className="inline align-text-bottom mr-1" />
              At least 8 characters
            </li>
            <li className={pw.length === 0 ? 'text-arena-500 dark:text-neon-200/55' : noLetter ? 'text-rose-600 dark:text-rose-300' : 'text-emerald-600 dark:text-emerald-300'}>
              <Icon name={pw.length === 0 ? 'radio_button_unchecked' : noLetter ? 'cancel' : 'check_circle'} size={11} className="inline align-text-bottom mr-1" />
              Contains a letter
            </li>
            <li className={pw.length === 0 ? 'text-arena-500 dark:text-neon-200/55' : noDigit ? 'text-rose-600 dark:text-rose-300' : 'text-emerald-600 dark:text-emerald-300'}>
              <Icon name={pw.length === 0 ? 'radio_button_unchecked' : noDigit ? 'cancel' : 'check_circle'} size={11} className="inline align-text-bottom mr-1" />
              Contains a digit
            </li>
          </ul>
        </div>

        {err && (
          <div className="rounded-lg bg-rose-50 dark:bg-rose-500/10 ring-1 ring-inset ring-rose-200/60 dark:ring-rose-400/30 text-rose-700 dark:text-rose-200 text-sm px-3 py-2">
            {err}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} disabled={busy}
            className="px-4 py-2 rounded-full text-[13px] font-semibold bg-white dark:bg-arena-700/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 text-arena-700 dark:text-neon-200/75 hover:bg-arena-50 dark:hover:bg-arena-700/60">
            Cancel
          </button>
          <button onClick={submit} disabled={busy || !valid}
            className="px-4 py-2 rounded-full text-[13px] font-bold bg-rose-600 hover:bg-rose-500 active:bg-rose-700 text-white shadow-[0_2px_10px_rgba(244,63,94,0.35)] inline-flex items-center gap-1.5 disabled:opacity-50">
            {busy ? <Icon name="progress_activity" size={14} className="animate-spin" /> : <Icon name="password" size={14} />}
            Reset &amp; sign-out everywhere
          </button>
        </div>
      </div>
    </div>
  );
}
